"use client";
import type { DocumentNode } from "graphql";
import {
  filterOptions,
  filterOptionsDataFragmentDoc,
  filterOptionsProperty,
  Maybe,
} from "@gql/graphql";
import { CmsEditable } from "@remkoj/optimizely-cms-react";
import { SearchFilterAdvance } from "@/components-non-cms/SearchFilterAdvance";
import type { FilterGroup } from "@/components-non-cms/SearchFilterAdvance/types";
import { useState, useEffect } from "react";
type FilterOptionsGroupDataProps = {
  data: Maybe<Maybe<filterOptionsProperty>[]> | undefined;
  inEditMode?: boolean;
};

interface FilterOptionsGroupProps
  extends React.FC<FilterOptionsGroupDataProps> {
  getDataFragment: () => [string, DocumentNode];
}

const FilterOptionsCmsComponent: FilterOptionsGroupProps = ({
  data,
  inEditMode = false,
}: FilterOptionsGroupDataProps) => {
  const {} = data || {};
  const [errorMsg, setErrorMsg] = useState("");
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);

  useEffect(() => {
    if (data) {
      const groups: FilterGroup[] = data.map((item) => ({
        label: item?.filterTitle || "",
        values: (item?.filterOptions ?? []).filter(
          (v): v is string => v != null
        ),
        selectedOptions: (item?.selectedOptions ?? []).filter(
          (v): v is string => v != null
        ),
      }));
      setFilterGroups(groups);
    }
  }, [data]);

  const updateFilterOptions = (
    filterOptions: any[],
    selectedOptions: any[]
  ) => {
    return filterOptions.map((filter) => {
      const match = selectedOptions.find((s) => s.label === filter.label);
      return {
        ...filter,
        selectedOptions: match ? match.values : filter.selectedOptions,
      };
    });
  };

  const onClickSearch = (selectedFilters: any[], searchValue: string) => {
    if (searchValue.length < 3) {
      setErrorMsg("Search text must be at least 3 characters long");
      return;
    }
    setErrorMsg("");
    const updatedFilterOptions = updateFilterOptions(
      filterGroups,
      selectedFilters
    );
    setFilterGroups(updatedFilterOptions);
    // 🔹 Helper to convert age label → range
    const getAgeRange = (label: string) => {
      const lower = label.toLowerCase().replace(/\s+/g, "");
      if (lower.includes("under")) return { agemin: 0, agemax: 5 };
      if (lower.includes("6-9")) return { agemin: 6, agemax: 9 };
      if (lower.includes("10-12")) return { agemin: 10, agemax: 12 };
      if (lower.includes("13-16")) return { agemin: 13, agemax: 16 };
      if (lower.includes("17-18")) return { agemin: 17, agemax: 18 };
      if (lower.includes("19")) return { agemin: 19, agemax: null }; // open-ended
      return { agemin: null, agemax: null };
    };

    const filterMap: Record<string, string> = {
      "Participant Type": "type",
      Activity: "activity",
      Gender: "gender",
      "Competition Type": "comptype",
      Age: "age",
    };

    const appSlug = (process.env.NEXT_PUBLIC_APP_SLUG ?? "prl").toLowerCase();
    const source =
      appSlug === "tfa" ? "touch-football" : "rugby-league";

    const query: Record<string, string> = {
      criteria: searchValue || "",
      type: "",
      activity: "",
      gender: "",
      agemin: "",
      agemax: "",
      comptype: "",
      source,
    };

    selectedFilters.forEach((f) => {
      const key = filterMap[f.label];
      const allValues = f.values || [];
      const selectedValues = f.values?.filter((v: string) => v !== "All") || [];

      // 🟢 Handle if all options are selected (or "All" is in selected)
      const allSelected =
        selectedValues.length === allValues.length - 1 ||
        f.values.includes("All");

      if (key === "age" && selectedValues.length > 0) {
        // 🔹 Compute min/max across selected age ranges
        const allRanges = selectedValues
          .map(getAgeRange)
          .filter((r: { agemin: null }) => r.agemin !== null);
        if (allRanges.length > 0) {
          const min = Math.min(
            ...allRanges.map((r: { agemin: any }) => r.agemin!)
          );
          const maxValues = allRanges
            .map((r: { agemax: any }) => r.agemax)
            .filter((v: any): v is number => v !== null);
          const max = maxValues.length > 0 ? Math.max(...maxValues) : null;
          query.agemin = min.toString();
          query.agemax = max !== null ? max.toString() : "";
        }
      } else if (key && f.label !== "Age") {
        // 🟢 If all options are selected, just send "All"
        if (allSelected) {
          query[key] = "all";
        } else if (selectedValues.length > 0) {
          query[key] = selectedValues.join(",").toLowerCase();
        }
      }
    });

    const queryString = new URLSearchParams(query).toString();

    const baseUrl =
      process.env.NODE_ENV === "production"
        ? "https://profile.mysideline.com.au"
        : "https://test-profile.mysideline.xyz";

    const portalUrl = `${baseUrl}/register/clubsearch?${queryString}`;
    window.open(portalUrl, "_blank", "noopener,noreferrer");
  };

  const onFilterApply = (selectedFilters: any, searchValue: any) => {
    const updatedFilterOptions = updateFilterOptions(
      filterGroups,
      selectedFilters
    );
    setFilterGroups(updatedFilterOptions);
    if (searchValue.length < 3) {
      setErrorMsg("Search text must be at least 3 characters long");
      return;
    }
    setErrorMsg("");
    onClickSearch(selectedFilters, searchValue);
  };
  return (
    <div className="absolute bottom-20 left-0 w-full flex justify-center">
      <div className="w-[80%] max-w-[1440px] flex flex-col items-center">
        <SearchFilterAdvance
          filterOptions={filterGroups}
          onClickSearch={onClickSearch}
          onFilterApply={onFilterApply}
          errorMessage={errorMsg}
        />
      </div>
    </div>
  );
};

FilterOptionsCmsComponent.displayName = "Filter Options Group";

FilterOptionsCmsComponent.getDataFragment = () => [
  "FilterOptionsDataProps",
  filterOptionsDataFragmentDoc,
];

// Attach the data fragment to the component
FilterOptionsCmsComponent.getDataFragment = (): [string, DocumentNode] => [
  "FilterOptionsDataProps",
  filterOptionsDataFragmentDoc,
];

export default FilterOptionsCmsComponent;
